/* assets/verlanglijst.js
 * Vijververlanglijstje (Sinterklaas + Kerst), werkstroom C, 20-10-2026; v2 (KISS) 21-10-2026.
 * Geladen door snippets/verlanglijst-laden.liquid, alleen als de schakelaar aan staat.
 *
 * Wat dit doet:
 * - hartjes (snippets/verlanglijst-hartje.liquid) op productkaart en productpagina: toevoegen en
 *   verwijderen via de App Proxy /apps/vp/verlanglijst (contract: bouwopdracht par. 3);
 * - de teller in de header (snippets/verlanglijst-header-link.liquid);
 * - een gastlijst koppelen aan de klant zodra die ingelogd is (POST /lijst/:token/claim);
 * - window.Verlanglijst voor assets/verlanglijst-beheer.js en assets/verlanglijst-deel.js;
 * - het event verlanglijst:gewijzigd na elk hartje (toevoegen of weghalen), zodat de beheerpagina meeloopt.
 *
 * Waarom de beheersleutel in localStorage staat en niet in een cookie: een App Proxy haalt Cookie en
 * Set-Cookie in beide richtingen weg (shopify.dev, app proxies). De sleutel gaat daarom mee in de
 * header X-Verlanglijst-Beheer en, bij schrijfacties, ook in de body als beheerToken. De server
 * bewaart alleen de hash. Contract zoals gebouwd door werkstroom B: retailz-server
 * docs/verlanglijst/BACKEND.md par. 3; afspraken van het thema: docs/verlanglijst/C-thema.md. Een ingelogde klant heeft de sleutel niet nodig: de server leest
 * logged_in_customer_id uit het ondertekende proxyverzoek.
 *
 * De pure functies onderaan (bepaalSeizoen, kortTitel, totaalCenten, deelLinks, ...) staan ook in
 * module.exports voor tests/verlanglijst.test.js. Houd bepaalSeizoen gelijk aan
 * snippets/verlanglijst-seizoen.liquid.
 */
(function (root) {
  'use strict';

  var OPSLAG_SLEUTEL = 'vl:v1';

  /* ── Pure functies (ook getest in node) ─────────────────────────────── */

  /* "2026-12-05" -> 20261205; alles wat geen JJJJ-MM-DD is -> 0 (= leeg, zoals in Liquid). */
  function datumGetal(tekst) {
    if (typeof tekst !== 'string') return 0;
    var t = tekst.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return 0;
    return parseInt(t.replace(/-/g, ''), 10);
  }

  /* Zelfde regels als snippets/verlanglijst-seizoen.liquid. */
  function bepaalSeizoen(vandaag, datums) {
    var d = datums || {};
    var sintTot = datumGetal(d.sintTot);
    var kerstVanaf = datumGetal(d.kerstVanaf);
    var kerstTot = datumGetal(d.kerstTot);
    if (kerstVanaf > 0 && kerstTot > 0 && vandaag >= kerstVanaf && vandaag <= kerstTot) return 'kerst';
    if (sintTot > 0 && vandaag <= sintTot) return 'sint';
    return 'neutraal';
  }

  /* Datum als getal in Europe/Amsterdam, ongeacht de tijdzone van het toestel. */
  function vandaagGetal(nu) {
    var delen = amsterdamDelen(nu || new Date());
    return delen.jaar * 10000 + delen.maand * 100 + delen.dag;
  }

  function amsterdamDelen(datum) {
    var f = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Amsterdam', year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23'
    });
    var uit = {};
    f.formatToParts(datum).forEach(function (p) { uit[p.type] = parseInt(p.value, 10); });
    return { jaar: uit.year, maand: uit.month, dag: uit.day, uur: uit.hour, minuut: uit.minute, seconde: uit.second };
  }

  /* Ms sinds epoch van een Amsterdamse wandtijd (houdt rekening met zomer/wintertijd). */
  function amsterdamNaarMs(jaar, maand, dag, uur, minuut) {
    var gok = Date.UTC(jaar, maand - 1, dag, uur, minuut);
    for (var i = 0; i < 2; i++) {
      var d = amsterdamDelen(new Date(gok));
      var alsUtc = Date.UTC(d.jaar, d.maand - 1, d.dag, d.uur, d.minuut);
      gok += Date.UTC(jaar, maand - 1, dag, uur, minuut) - alsUtc;
    }
    return gok;
  }

  /* Tijd tot de cut-off op de laatste besteldag. cutoff is de thema-instelling delivery_cutoff ("23.59"). */
  function resterendeTijd(nuMs, laatsteDatum, cutoff) {
    var g = datumGetal(laatsteDatum);
    if (!g) return null;
    var delen = String(cutoff || '23.59').split(/[.:]/);
    var uur = Math.min(23, parseInt(delen[0], 10) || 23);
    var minuut = Math.min(59, parseInt(delen[1], 10) || 0);
    var eind = amsterdamNaarMs(Math.floor(g / 10000), Math.floor(g / 100) % 100, g % 100, uur, minuut);
    var rest = eind - nuMs;
    if (rest <= 0) return { voorbij: true, dagen: 0, uren: 0, minuten: 0, eindMs: eind };
    return {
      voorbij: false,
      dagen: Math.floor(rest / 86400000),
      uren: Math.floor((rest % 86400000) / 3600000),
      minuten: Math.floor((rest % 3600000) / 60000),
      eindMs: eind
    };
  }

  /* Deellinks. De sjablonen komen uit de taalbestanden (werkstroom F) met __LINK__ als plek voor de
     link; ontbreekt die, dan komt de link erachter. Alles via encodeURIComponent: vrije tekst. */
  function vulLink(sjabloon, url) {
    var t = String(sjabloon || '');
    if (t.indexOf('__LINK__') === -1) return (t ? t + ' ' : '') + url;
    return t.split('__LINK__').join(url);
  }

  function deelLinks(url, sjablonen) {
    var s = sjablonen || {};
    return {
      whatsapp: 'https://wa.me/?text=' + encodeURIComponent(vulLink(s.whatsapp, url)),
      mail: 'mailto:?subject=' + encodeURIComponent(s.mailOnderwerp || '') + '&body=' + encodeURIComponent(vulLink(s.mailBody, url)),
      facebook: 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(url)
    };
  }

  /* Alleen tokens die we zelf zouden maken: URL-veilig, lang genoeg. Al het andere is geen stand. */
  function geldigToken(t) {
    return typeof t === 'string' && /^[A-Za-z0-9_-]{16,128}$/.test(t);
  }

  function normaliseerStand(ruw) {
    var s = { token: null, beheerToken: null, geclaimd: false, items: {} };
    if (!ruw || typeof ruw !== 'object') return s;
    if (geldigToken(ruw.token)) s.token = ruw.token;
    if (s.token && typeof ruw.beheerToken === 'string' && ruw.beheerToken.length <= 256) s.beheerToken = ruw.beheerToken;
    s.geclaimd = ruw.geclaimd === true;
    if (s.token && ruw.items && typeof ruw.items === 'object') {
      Object.keys(ruw.items).forEach(function (variant) {
        var id = ruw.items[variant];
        if (/^\d+$/.test(variant) && (typeof id === 'string' || typeof id === 'number') && String(id).length <= 128) {
          s.items[variant] = String(id);
        }
      });
    }
    return s;
  }

  /* Variant -> item-id uit een lijstantwoord van GET /lijst/:token. */
  function itemsUitLijst(lijst) {
    var uit = {};
    var items = (lijst && lijst.items) || [];
    items.forEach(function (it) {
      var variant = String(it.variantId || '').replace(/^gid:\/\/shopify\/ProductVariant\//, '');
      if (/^\d+$/.test(variant) && it.id != null) uit[variant] = String(it.id);
    });
    return uit;
  }

  function aantalItems(stand) {
    return Object.keys((stand && stand.items) || {}).length;
  }

  /* Korte titel: alleen het deel voor " - " (de SEO-staart eraf), zoals op de productkaart.
     "Takazumi Complete Mix 20 liter - Allround drijvend koivoer" -> "Takazumi Complete Mix 20 liter". */
  function kortTitel(titel) {
    var t = String(titel == null ? '' : titel).replace(/\s+/g, ' ').trim();
    var kort = t.split(' - ')[0].trim();
    return kort || t;
  }

  /* Totaal van een lijst in centen: prijs x aantal, alleen items met een geldige prijs. */
  function totaalCenten(items) {
    return (items || []).reduce(function (som, it) {
      var p = parseInt(it && (it.prijs != null ? it.prijs : it.price), 10);
      var n = Math.max(1, parseInt(it && it.aantal, 10) || 1);
      return isFinite(p) && p >= 0 ? som + p * n : som;
    }, 0);
  }

  /* Client-sleutel van een gever (afstrepen op de deelpagina): 16 willekeurige bytes als hex. */
  function sleutelUitBytes(bytes) {
    return Array.prototype.map.call(bytes || [], function (x) { return ('0' + (x & 255).toString(16)).slice(-2); }).join('');
  }

  var pure = {
    datumGetal: datumGetal,
    bepaalSeizoen: bepaalSeizoen,
    vandaagGetal: vandaagGetal,
    resterendeTijd: resterendeTijd,
    deelLinks: deelLinks,
    vulLink: vulLink,
    geldigToken: geldigToken,
    normaliseerStand: normaliseerStand,
    itemsUitLijst: itemsUitLijst,
    aantalItems: aantalItems,
    kortTitel: kortTitel,
    totaalCenten: totaalCenten,
    sleutelUitBytes: sleutelUitBytes
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = pure;
  }
  if (typeof document === 'undefined') return;

  /* ── Browser ────────────────────────────────────────────────────────── */

  if (root.Verlanglijst) return; /* init-guard: één keer per pagina */

  var configEl = document.getElementById('vl-config');
  if (!configEl) return;
  var CONFIG;
  try { CONFIG = JSON.parse(configEl.textContent); } catch (e) { return; }
  var T = CONFIG.teksten || {};
  var BASE = String(CONFIG.proxyBase || '/apps/verlanglijst').replace(/\/+$/, '');
  var TEST_QS = CONFIG.testSleutel ? 'vl_test=' + encodeURIComponent(CONFIG.testSleutel) : '';

  /* Testers kunnen een datum nabootsen met ?vl_datum=2026-12-10 (alleen voor de JS-kant:
     aftelklok, adventsvakjes, seizoensklasse). Gewone bezoekers niet. */
  var nagebootst = 0;
  if (CONFIG.tester) {
    var m = /[?&]vl_datum=(\d{4}-\d{2}-\d{2})/.exec(location.search);
    if (m) nagebootst = datumGetal(m[1]);
  }

  function leesStand() {
    try { return normaliseerStand(JSON.parse(localStorage.getItem(OPSLAG_SLEUTEL) || 'null')); }
    catch (e) { return normaliseerStand(null); }
  }
  function schrijfStand(s) {
    try { localStorage.setItem(OPSLAG_SLEUTEL, JSON.stringify(s)); } catch (e) { /* privémodus: alleen deze pagina */ }
    stand = s;
    werkUiBij();
  }
  var stand = leesStand();

  /* Fout met status, zodat de pagina's per geval een nette tekst kunnen tonen. */
  function ApiFout(status, code) {
    this.status = status;
    this.code = code || '';
  }

  /* opties.keepalive: het verzoek loopt door als de bezoeker meteen naar WhatsApp gaat (deelvenster). */
  function api(methode, pad, body, extra) {
    var kop = { 'Accept': 'application/json' };
    if (stand.beheerToken) kop['X-Verlanglijst-Beheer'] = stand.beheerToken;
    var opties = { method: methode, headers: kop, credentials: 'same-origin' };
    if (extra && extra.keepalive) opties.keepalive = true;
    if (body !== undefined) {
      var inhoud = Object.assign({}, body);
      if (stand.beheerToken && methode !== 'GET') inhoud.beheerToken = stand.beheerToken;
      kop['Content-Type'] = 'application/json';
      opties.body = JSON.stringify(inhoud);
    }
    var url = BASE + pad + (TEST_QS ? (pad.indexOf('?') >= 0 ? '&' : '?') + TEST_QS : '');
    return fetch(url, opties).then(function (res) {
      if (res.status === 204) return {};
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok) throw new ApiFout(res.status, data && data.error);
        return data;
      });
    }, function () { throw new ApiFout(0, 'netwerk'); });
  }

  /* Lijst ophalen voor beheer. Komt de header X-Verlanglijst-Beheer niet door de proxy, dan geeft de
     GET 404; met een beheersleutel proberen we dan POST /ophalen (sleutel in de body, terugval van B). */
  function haalLijst() {
    var pad = '/lijst/' + encodeURIComponent(stand.token);
    return api('GET', pad).catch(function (fout) {
      if (fout.status === 404 && stand.beheerToken) return api('POST', pad + '/ophalen', {});
      throw fout;
    }).then(function (data) { return data.lijst || data; });
  }

  function foutTekst(fout) {
    if (fout && fout.status === 429) return T.teVeel;
    if (fout && fout.status === 0) return T.netwerk || T.fout;
    if (fout && (fout.status === 401 || fout.status === 403)) return T.geenToegang || T.fout;
    if (fout && fout.code === 'VARIANT_NIET_BESCHIKBAAR') return T.productWeg || T.fout;
    return T.fout;
  }

  /* Maakt zo nodig een lijst aan. Ingelogd geeft de server de bestaande lijst van de klant terug. */
  var lijstBelofte = null;
  function zorgVoorLijst() {
    if (stand.token) return Promise.resolve(stand.token);
    if (lijstBelofte) return lijstBelofte;
    /* De server eist een naam. Ingelogd: de voornaam; gast: een neutrale naam die de beheerpagina
       als "nog niet ingevuld" behandelt. */
    var naam = String(CONFIG.voornaam || '').trim().slice(0, 60) || T.standaardNaam || 'Vijverliefhebber';
    lijstBelofte = api('POST', '/lijst', { naam: naam }).then(function (data) {
      var bron = data.lijst || data;
      if (!geldigToken(bron.token)) throw new ApiFout(500, 'geen_token');
      var nieuw = normaliseerStand({
        token: bron.token,
        beheerToken: data.beheerToken || bron.beheerToken || null,
        geclaimd: !!CONFIG.ingelogd,
        items: itemsUitLijst(bron)
      });
      schrijfStand(nieuw);
      return nieuw.token;
    }).finally(function () { lijstBelofte = null; });
    return lijstBelofte;
  }

  function voegToe(variantId, extra) {
    return zorgVoorLijst().then(function (token) {
      return api('POST', '/lijst/' + token + '/items', Object.assign({ variantId: String(variantId), aantal: 1 }, extra || {}));
    }).then(function (data) {
      var item = data.item || data;
      if (item.id == null) throw new ApiFout(500, 'geen_item');
      var s = normaliseerStand(stand);
      s.items[String(variantId)] = String(item.id);
      schrijfStand(s);
      return item;
    });
  }

  function verwijder(variantId) {
    var itemId = stand.items[String(variantId)];
    if (!stand.token || !itemId) return Promise.resolve();
    return api('DELETE', '/lijst/' + stand.token + '/items/' + encodeURIComponent(itemId)).catch(function (fout) {
      if (fout.status !== 404) throw fout; /* al weg op de server: lokaal ook weghalen */
    }).then(function () {
      var s = normaliseerStand(stand);
      delete s.items[String(variantId)];
      schrijfStand(s);
    });
  }

  /* De server is de waarheid. Na een GET van de beheerpagina zetten we de lokale stand gelijk. */
  function synchroniseer(lijst) {
    var s = normaliseerStand(stand);
    s.items = itemsUitLijst(lijst);
    schrijfStand(s);
  }

  function vergeet() {
    try { localStorage.removeItem(OPSLAG_SLEUTEL); } catch (e) { /* niets */ }
    stand = normaliseerStand(null);
    werkUiBij();
  }

  /* ── Meldingen (aria-live) ─────────────────────────────────────────── */
  var meldEl = null;
  var meldTimer = null;
  function meld(tekst, link) {
    if (!meldEl) {
      meldEl = document.createElement('div');
      meldEl.className = 'vl-melding';
      meldEl.setAttribute('role', 'status');
      meldEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(meldEl);
    }
    meldEl.textContent = '';
    var p = document.createElement('span');
    p.textContent = tekst;
    meldEl.appendChild(p);
    if (link) {
      var a = document.createElement('a');
      a.href = CONFIG.beheerUrl;
      a.textContent = T.bekijk;
      meldEl.appendChild(a);
    }
    meldEl.classList.add('is-zichtbaar');
    clearTimeout(meldTimer);
    meldTimer = setTimeout(function () { meldEl.classList.remove('is-zichtbaar'); }, 4000);
  }

  /* ── Hartjes en teller ─────────────────────────────────────────────── */
  function pdpVariant(knop) {
    var keuze = document.querySelector('[data-variant-select]');
    if (knop.getAttribute('data-vl-plaats') === 'pdp' && keuze && /^\d+$/.test(keuze.value)) return keuze.value;
    return knop.getAttribute('data-vl-variant');
  }

  /* kort: alleen het deel voor " - " (zoals de producttitel op de kaart), voor de melding. */
  function metProduct(sjabloon, knop, kort) {
    var titel = knop.getAttribute('data-vl-titel') || '';
    if (kort) titel = titel.split(' - ')[0];
    return String(sjabloon || '').split('__PRODUCT__').join(titel).replace(/\s+/g, ' ').trim();
  }

  function werkHartjeBij(knop) {
    var variant = pdpVariant(knop);
    var aan = !!stand.items[variant];
    knop.setAttribute('aria-pressed', aan ? 'true' : 'false');
    var tekst = knop.querySelector('[data-vl-hartje-tekst]');
    if (tekst) tekst.textContent = aan ? T.verwijderen : T.toevoegen;
    if (knop.getAttribute('data-vl-plaats') !== 'pdp' && T.ariaToevoegen) {
      knop.setAttribute('aria-label', metProduct(aan ? T.ariaVerwijderen : T.ariaToevoegen, knop));
    }
  }

  function werkUiBij() {
    var hartjes = document.querySelectorAll('[data-vl-hartje]');
    for (var i = 0; i < hartjes.length; i++) werkHartjeBij(hartjes[i]);
    var n = aantalItems(stand);
    var tellers = document.querySelectorAll('[data-vl-teller]');
    for (var j = 0; j < tellers.length; j++) {
      tellers[j].textContent = String(n);
      tellers[j].hidden = n === 0;
    }
    var links = document.querySelectorAll('[data-vl-header-link]');
    for (var k = 0; k < links.length; k++) {
      links[k].setAttribute('aria-label', n && T.ariaTeller ? T.ariaTeller.split('__AANTAL__').join(String(n)) : (T.headerLabel || 'Verlanglijst'));
    }
  }

  document.addEventListener('click', function (e) {
    var knop = e.target.closest && e.target.closest('[data-vl-hartje]');
    if (!knop) return;
    e.preventDefault();
    e.stopPropagation();
    if (knop.getAttribute('aria-busy') === 'true') return;
    var variant = pdpVariant(knop);
    if (!/^\d+$/.test(variant || '')) return;
    var wasAan = !!stand.items[variant];
    knop.setAttribute('aria-busy', 'true');
    knop.setAttribute('aria-pressed', wasAan ? 'false' : 'true'); /* optimistisch */
    (wasAan ? verwijder(variant) : voegToe(variant)).then(function () {
      /* Op de beheerpagina zelf geen link "Bekijk je lijstje": daar sta je al. */
      var opBeheer = location.pathname === String(CONFIG.beheerUrl || '').split('?')[0];
      meld(metProduct(wasAan ? T.verwijderd : T.toegevoegd, knop, true), !wasAan && !opBeheer);
      if (!wasAan) document.dispatchEvent(new CustomEvent('verlanglijst:toegevoegd', { detail: { variantId: variant } }));
      document.dispatchEvent(new CustomEvent('verlanglijst:gewijzigd', { detail: { variantId: variant, aan: !wasAan } }));
    }, function (fout) {
      if (!wasAan && fout && fout.status === 404 && fout.code === 'NIET_GEVONDEN' && !CONFIG.ingelogd) {
        /* Lijst bestaat niet meer of de sleutel hoort er niet bij: begin schoon, de klant kan opnieuw
           klikken. Alleen bij toevoegen; een 404 bij verwijderen gaat over het item (QA G 20-10-2026). */
        vergeet();
      }
      meld(foutTekst(fout), false);
    }).then(function () {
      knop.removeAttribute('aria-busy');
      werkUiBij();
    });
  }, true);

  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('[data-variant-select]')) werkUiBij();
  });

  /* Andere tabbladen houden de stand gelijk. */
  window.addEventListener('storage', function (e) {
    if (e.key === OPSLAG_SLEUTEL) { stand = leesStand(); werkUiBij(); }
  });

  /* Gastlijst koppelen aan het account, één keer, zodra de klant ingelogd is. */
  function claimAlsNodig() {
    if (!CONFIG.ingelogd || !stand.token || stand.geclaimd) return;
    api('POST', '/lijst/' + stand.token + '/claim', {}).then(function () {
      var s = normaliseerStand(stand);
      s.geclaimd = true;
      schrijfStand(s);
    }, function () { /* volgende pagina opnieuw; nooit blokkeren */ });
  }

  root.Verlanglijst = {
    config: CONFIG,
    teksten: T,
    stand: function () { return stand; },
    api: api,
    ApiFout: ApiFout,
    foutTekst: foutTekst,
    zorgVoorLijst: zorgVoorLijst,
    haalLijst: haalLijst,
    voegToe: voegToe,
    verwijder: verwijder,
    synchroniseer: synchroniseer,
    vergeet: vergeet,
    meld: meld,
    werkUiBij: function () { werkUiBij(); },
    /* Client-sleutel voor afstrepen: een per browser, in localStorage (zonder opslag: per pagina). */
    klient: function () {
      var s = '';
      try { s = localStorage.getItem('vl:klient') || ''; } catch (e) { /* privémodus */ }
      if (!/^[a-f0-9]{32}$/.test(s) && window.crypto && crypto.getRandomValues) {
        s = sleutelUitBytes(crypto.getRandomValues(new Uint8Array(16)));
        try { localStorage.setItem('vl:klient', s); } catch (e) { /* privémodus */ }
      }
      return s;
    },
    vandaag: function () { return nagebootst || vandaagGetal(new Date()); },
    nuMs: function () {
      if (!nagebootst) return Date.now();
      return amsterdamNaarMs(Math.floor(nagebootst / 10000), Math.floor(nagebootst / 100) % 100, nagebootst % 100, 12, 0);
    },
    nagebootst: nagebootst,
    pure: pure
  };

  function start() {
    werkUiBij();
    claimAlsNodig();
    document.dispatchEvent(new CustomEvent('verlanglijst:klaar'));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})(typeof window !== 'undefined' ? window : this);
